import { generateClient } from "aws-amplify/api";
import { downloadData } from "aws-amplify/storage";

// What a ProcessedModel V2 output's forecasts hold — parameters, their unit / name, and which series kinds each
// has — so a composed forecast offers only the traces and bands an output really carries.
// s3Triggerfunc writes it on every upload (amplify/function/s3Triggerfunc/src/seriesMetaData.js is the twin of
// the describe logic below — keep the two in step).

const client = generateClient();

export interface SeriesKinds {
  mean: boolean;
  control: boolean;
  value: boolean;
  obs: boolean;
  median: boolean;
  qc?: boolean; // has a qc_ flag column (absent on descriptions written before it existed)
  percentiles: number[];
  members: number;
  other?: string[];
}

export interface SeriesParameter {
  name?: string;
  unit?: string;
  description?: string;
  kind?: string; // forecast | observation | derived (the file's `kind` row)
  qcCodes?: Record<string, string>; // bit value → label, from the `qc_codes` row
  kinds: SeriesKinds;
  columns: string[];
}

export interface SeriesMetaData {
  version: number;
  format: "csv" | "json";
  forecastIssued: string | null;
  window?: { start: string | null; end: string | null }; // historical outputs: the run's window
  parameters: Record<string, SeriesParameter>;
  start?: string | null; // per issue only
  end?: string | null;
  rows?: number;
  recordForecastIssued?: string; // output level: the issue it was described from
  describedFrom: string;
  describedAt: string;
}

// ── Describe (mirror of s3Triggerfunc/src/seriesMetaData.js) ───────────────────────────────────────────────

const PREFIX_RULES: Array<[RegExp, string]> = [
  [/^p_(\d{2,3})_(.+)$/i, "percentile"],
  [/^m_(\d{2,})_(.+)$/i, "member"],
  [/^mean_(.+)$/i, "mean"],
  [/^c_(.+)$/i, "control"],
  [/^obs_(.+)$/i, "obs"],
  [/^median_(.+)$/i, "median"],
  [/^min_(.+)$/i, "min"],
  [/^max_(.+)$/i, "max"],
  [/^std_(.+)$/i, "std"],
  [/^qc_(.+)$/i, "qc"], // quality flags for the parameter's values, not a series
];

type Classified = { kind: string; n?: number; parameter?: string };

const classifyColumn = (name: string): Classified => {
  for (const [rule, kind] of PREFIX_RULES) {
    const m = rule.exec(name);
    if (!m) continue;
    if (kind === "percentile" || kind === "member") return { kind, parameter: m[2], n: Number(m[1]) };
    return { kind, parameter: m[1] };
  }
  return { kind: "value", parameter: name };
};

const classifySeriesKey = (key: string): Classified => {
  const k = String(key).trim().toLowerCase();
  if (k === "mean") return { kind: "mean" };
  if (k === "control" || k === "c") return { kind: "control" };
  if (["value", "hourly", "pred", "predicted"].includes(k)) return { kind: "value" };
  if (["actual", "obs", "observed"].includes(k)) return { kind: "obs" };
  if (["median", "min", "max", "std"].includes(k)) return { kind: k };
  const p = /^[pq](\d{1,3})$/.exec(k);
  if (p) return { kind: "percentile", n: Number(p[1]) };
  const m = /^m[_-]?(\d{1,3})$/.exec(k);
  if (m) return { kind: "member", n: Number(m[1]) };
  const x = Number(k);
  if (k !== "" && !Number.isNaN(x) && x >= 0 && x <= 1) return { kind: "percentile", n: Math.round(x * 100) };
  return { kind: "other" };
};

const emptyParameter = (): SeriesParameter => ({
  kinds: { mean: false, control: false, value: false, obs: false, median: false, qc: false, percentiles: [], members: 0 },
  columns: [],
});

const addKind = (param: SeriesParameter, { kind, n }: Classified, column: string) => {
  const kinds = param.kinds as any;
  if (kind === "percentile") {
    if (!kinds.percentiles.includes(n)) kinds.percentiles.push(n);
  } else if (kind === "member") {
    kinds.members += 1;
  } else if (typeof kinds[kind] === "boolean") {
    kinds[kind] = true;
  } else {
    kinds.other = [...(kinds.other || []), column];
  }
  param.columns.push(column);
};

const isoZ = (raw: unknown): string | null => {
  const text = String(raw ?? "").trim().replace(" ", "T");
  if (!/^\d{4}-\d{2}-\d{2}/.test(text)) return null;
  const d = new Date(/(?:z|[+-]\d{2}:?\d{2})$/i.test(text) ? text : `${text}Z`);
  return Number.isNaN(d.valueOf()) ? null : d.toISOString();
};

const span = (times: Array<string | null>) => {
  const sorted = times.filter((t): t is string => !!t).sort();
  return { start: sorted[0] ?? null, end: sorted[sorted.length - 1] ?? null, rows: sorted.length };
};

const splitCsv = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += ch;
  }
  if (cell !== "" || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
};

/** `qc_codes` cell: JSON object of bit value → label, or null. */
const parseQcCodes = (raw: string): Record<string, string> | null => {
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    return Object.fromEntries(Object.entries(parsed).map(([bit, label]) => [bit, String(label)]));
  } catch {
    return null;
  }
};

const describeCsv = (text: string) => {
  const rows = splitCsv(text);
  const headerIndex = rows.findIndex(
    (r) => /datetime/i.test(r[0] || "") || /^(timestamp|time|date)$/i.test((r[0] || "").trim())
  );
  if (headerIndex === -1) throw new Error("no DateTime header row");
  const fileMeta: Record<string, string> = {};
  const paramMeta: Record<string, Partial<SeriesParameter>> = {};
  for (const r of rows.slice(0, headerIndex)) {
    const [key, second, third] = r.map((c) => c.trim());
    if (!key) continue;
    if (r.length === 2) fileMeta[key] = second;
    else if (r.length >= 3 && ["unit", "name", "description", "kind"].includes(key)) {
      paramMeta[second.toLowerCase()] = { ...paramMeta[second.toLowerCase()], [key]: third };
    } else if (r.length >= 3 && key === "qc_codes") {
      const codes = parseQcCodes(third);
      if (codes) paramMeta[second.toLowerCase()] = { ...paramMeta[second.toLowerCase()], qcCodes: codes };
    }
  }
  const parameters: Record<string, SeriesParameter> = {};
  for (const column of rows[headerIndex].slice(1).map((c) => c.trim())) {
    const info = classifyColumn(column);
    const key = info.parameter as string;
    parameters[key] = parameters[key] || { ...emptyParameter(), ...(paramMeta[key.toLowerCase()] || {}) };
    addKind(parameters[key], info, column);
  }
  return {
    format: "csv" as const,
    forecastIssued: fileMeta.forecast_issued ? isoZ(fileMeta.forecast_issued) || fileMeta.forecast_issued : null,
    // Historical outputs: the window a run covers (spec §3), instead of an issue time
    ...(fileMeta.window_start || fileMeta.window_end
      ? { window: { start: isoZ(fileMeta.window_start), end: isoZ(fileMeta.window_end) } }
      : {}),
    parameters,
    ...span(rows.slice(headerIndex + 1).map((r) => isoZ(r[0]))),
  };
};

const describeJson = (payload: any) => {
  const parameters: Record<string, SeriesParameter> = {};
  const times: Array<string | null> = [];
  for (const [name, entry] of Object.entries<any>(payload?.models || {})) {
    if (!entry || typeof entry.data !== "object") continue;
    const param = (parameters[name] = emptyParameter());
    if (entry.variation) param.name = entry.variation;
    if (entry.yUnit) param.unit = entry.yUnit;
    const keys = new Set<string>();
    for (const [ts, series] of Object.entries<any>(entry.data)) {
      times.push(isoZ(ts));
      if (series && typeof series === "object") Object.keys(series).forEach((k) => keys.add(k));
      else keys.add("value");
    }
    keys.forEach((k) => addKind(param, classifySeriesKey(k), k));
  }
  return { format: "json" as const, forecastIssued: payload?.forecast_issued ?? null, parameters, ...span(times) };
};

/** Describe a forecast file's text; `key` picks the format by extension (.csv, else JSON). */
export function describeForecastText(text: string, key: string): SeriesMetaData {
  const described = /\.csv$/i.test(key) ? describeCsv(text) : describeJson(JSON.parse(text));
  Object.values(described.parameters).forEach((p) => p.kinds.percentiles.sort((a, b) => a - b));
  return { version: 1, ...described, describedFrom: key, describedAt: new Date().toISOString() };
}

// ── Lookup ───────────────────────────────────────────────────────────────────────────────────────────────

const parseJson = (raw: unknown): any => {
  let value = raw;
  for (let i = 0; i < 2 && typeof value === "string"; i++) {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value && typeof value === "object" ? value : null;
};

/**
 * What an output's forecasts hold: its stored seriesMetaData, else its newest ForecastRecord's, else — for
 * outputs whose files predate s3Triggerfunc describing them — read the newest file once and describe it.
 * That last fallback costs one download, and only while nothing is stored: the result is written back to the
 * record and the output (best effort — owners and Admins can; org members just use the computed value), so
 * each output is read at most once. Null when the output has no forecasts.
 */
export async function describeForecastOutput(outputParamId: string): Promise<SeriesMetaData | null> {
  const { data } = (await client.graphql({
    query: /* GraphQL */ `
      query DescribeForecastOutput($id: ID!) {
        getProcessedModelOutputParam(id: $id) {
          id
          seriesMetaData
          forecasts(sortDirection: DESC, limit: 1) {
            items { id forecastIssued s3Key seriesMetaData }
          }
        }
      }
    `,
    variables: { id: outputParamId },
  })) as any;
  const output = data?.getProcessedModelOutputParam;
  if (!output) return null;

  const stored = parseJson(output.seriesMetaData);
  if (stored?.parameters) return stored as SeriesMetaData;

  const latest = output.forecasts?.items?.[0];
  if (!latest?.s3Key) return null;
  const fromRecord = parseJson(latest.seriesMetaData);
  if (fromRecord?.parameters) {
    void saveOutputDescription(outputParamId, latest.forecastIssued, fromRecord);
    return fromRecord as SeriesMetaData;
  }

  // Fallback: describe the newest file (see the doc comment for why this stays cheap).
  const result = await downloadData({ path: latest.s3Key }).result;
  const described = describeForecastText(await result.body.text(), latest.s3Key);
  void saveRecordDescription(latest.id, described);
  void saveOutputDescription(outputParamId, latest.forecastIssued, described);
  return described;
}

const saveRecordDescription = async (recordId: string, meta: SeriesMetaData) => {
  try {
    await client.graphql({
      query: /* GraphQL */ `
        mutation SaveForecastRecordSeries($input: UpdateForecastRecordInput!) {
          updateForecastRecord(input: $input) { id }
        }
      `,
      variables: {
        input: { id: recordId, seriesMetaData: JSON.stringify(meta), seriesStart: meta.start, seriesEnd: meta.end },
      },
    });
  } catch {
    // Not the owner (org member): the computed description is still used, just not stored.
  }
};

const saveOutputDescription = async (outputParamId: string, recordForecastIssued: string, meta: SeriesMetaData) => {
  const { start, end, rows, ...outputLevel } = meta;
  try {
    await client.graphql({
      query: /* GraphQL */ `
        mutation SaveOutputSeries(
          $input: UpdateProcessedModelOutputParamInput!
          $condition: ModelProcessedModelOutputParamConditionInput
        ) {
          updateProcessedModelOutputParam(input: $input, condition: $condition) { id }
        }
      `,
      variables: {
        input: { id: outputParamId, seriesMetaData: JSON.stringify({ ...outputLevel, recordForecastIssued }) },
        // Only fill an empty description: s3Triggerfunc may have described a newer issue since we read it
        condition: { seriesMetaData: { attributeExists: false } },
      },
    });
  } catch {
    // As above: best effort — also lands here when the condition fails (a description already exists).
  }
};

/** The first parameter — the one the composed-forecast chart draws as its base model. */
export const primaryParameter = (meta: SeriesMetaData | null): SeriesParameter | null =>
  meta ? Object.values(meta.parameters)[0] ?? null : null;
